import React from 'react';

import { formatMoney } from '@betterment/core';
import { Text, VStack } from '@betterment/samba-react';
import moment from 'moment';

import { HouseholdAccountsQuery } from './graphql/query.householdAccounts.generated';

import { PurposeStatus } from '../../../graphql/schema';

type Purpose = HouseholdAccountsQuery['household']['accounts'][number]['envelope']['purpose'];

const isRetirementIncome = (purpose: Purpose): purpose is Extract<Purpose, { __typename: 'PurposeRetirementIncome' }> =>
  purpose?.__typename === 'PurposeRetirementIncome';

const isRetirementGrowth = (purpose: Purpose): purpose is Extract<Purpose, { __typename: 'PurposeRetirementGrowth' }> =>
  purpose?.__typename === 'PurposeRetirementGrowth';

const isMajorPurchase = (purpose: Purpose): purpose is Extract<Purpose, { __typename: 'PurposeMajorPurchase' }> =>
  purpose?.__typename === 'PurposeMajorPurchase';

const isEmergencyFund = (purpose: Purpose): purpose is Extract<Purpose, { __typename: 'PurposeEmergencyFund' }> =>
  purpose?.__typename === 'PurposeEmergencyFund';

const getTargetDateOrAge = (purpose: Purpose): string => {
  if (isRetirementGrowth(purpose)) {
    return `age ${purpose.ageAtCompletion}`;
  }

  if (isRetirementIncome(purpose) || isMajorPurchase(purpose) || isEmergencyFund(purpose)) {
    return moment(purpose.targetDate).format('MMM YYYY');
  }

  return '';
};

const getOnTrackStatus = (purpose: Purpose): string => {
  const status = purpose?.purposeStatus;
  switch (status) {
    case PurposeStatus.SavingEnough:
    case PurposeStatus.SavedEnough:
    case PurposeStatus.OnTrack:
      return 'On track';
    case PurposeStatus.NotSavingEnough:
    case PurposeStatus.OffTrack:
      return 'Off track';
    case PurposeStatus.Finished:
      return 'Goal achieved';
    case PurposeStatus.TimePassed:
      return 'Goal missed';
    case PurposeStatus.NoTarget:
      return '';
    default:
      return status ?? '';
  }
};

const getTargetPrefix = (purpose: Purpose): string => {
  if (isRetirementIncome(purpose)) {
    if (getOnTrackStatus(purpose) === 'On track') return 'Lasts through';
    if (getOnTrackStatus(purpose) === 'Off track') return 'Depletes by';
    return '';
  }

  return 'By';
};

const getTargetAmountDisplay = (purpose: Purpose): string => {
  if (isRetirementIncome(purpose) && purpose.projection?.recommendedMonthlyOutflows) {
    const monthlySpending = formatMoney(purpose.projection.recommendedMonthlyOutflows);
    return `Withdrawing ${monthlySpending}/month`;
  }

  return purpose?.targetAmount ? formatMoney(purpose.targetAmount) : '—';
};

export const TargetField: React.FC<{ purpose: Purpose }> = ({ purpose }) => {
  const targetDateOrAge = getTargetDateOrAge(purpose);
  const targetPrefix = getTargetPrefix(purpose);
  const targetDateString = `${targetPrefix}  ${targetDateOrAge}`;

  const targetAmount = getTargetAmountDisplay(purpose);

  const trackingStatus = getOnTrackStatus(purpose);
  const trackingStatusDisplay = trackingStatus ? `• ${trackingStatus}` : '';

  return (
    <VStack>
      <Text variant="text100">{targetAmount}</Text>
      <Text variant="text50" color="text.weak">
        {`${targetDateString} ${trackingStatusDisplay}`}
      </Text>
    </VStack>
  );
};
